import type { IValidationsModel } from "./validations-model";

/**
 * Which specialist domain a validation falls into when fixed by the AI.
 * The backend stamps a stable {@link IValidationsModel.code} (KV####); this
 * module is the single source of truth that maps that code to a fix domain.
 * Imported by BOTH the designer Issues panel (button gating) and the
 * mcp-chat-server orchestrator (fix routing) so the two never drift.
 */
export type ValidationFixCategory = "binder" | "wirer" | "designer" | "condition";

export interface ValidationFixability {
  readonly category: ValidationFixCategory;
  /**
   * Whether an AI fixer path is SHIPPED for this category yet. The Issues panel
   * shows "Fix with AI" only when this is true, so a button never appears for a
   * category the server cannot actually fix. Flip a category's codes to `true`
   * as each fixer phase lands (Phase 1 ships binder only; wirer/designer/
   * condition follow). `category` is always correct regardless of this flag.
   */
  readonly fixable: boolean;
}

/**
 * code → fix domain. Codes here without `fixable: true` are categorized (so the
 * router knows their domain) but not yet user-actionable. Keep in lockstep with
 * the backend stamping in ValidationExtensions.cs and KExceptionMessages.cs.
 */
const VALIDATION_FIX_MAP: Readonly<Record<string, ValidationFixability>> = {
  // Binder. Invariant: fixable:true ⟺ the SHIPPED fixer handles this code.
  // Phase 1 ships the empty-action-parameter fixer (reconstruct attachment +
  // runBinder Flow A). KV1012 ("The parameter {0} is not set.") and KV1170
  // ("The required parameter is not set", emitted per-parameter by the backend's
  // SymbolModel.IsValid) are the SAME logical error — a required action input
  // parameter the binder left empty — via two validator paths; both are stamped
  // (code + parameterName) and fixable.
  //
  // KV1008 / KV1017 are STALE-REFERENCE rebinds: the AI bound a parameter to a
  // field / screen-input that no longer exists (hallucinated `current.HastaId`,
  // `screenInput:Id`). The fixer analyzes the screen (host containment, table
  // columns, screen inputs) and rebinds to the sensible EXISTING source — same
  // binder pipeline (reconstruct attachment + runBinder) as the empty-param fill.
  KV1012: { category: "binder", fixable: true }, // "The parameter {0} is not set."
  KV1170: { category: "binder", fixable: true }, // "The required parameter is not set"
  KV1008: { category: "binder", fixable: true }, // "Target field '{1}' of action '{0}' no longer exists."
  KV1017: { category: "binder", fixable: true }, // "Parameter '{0}' of screen input '{1}' no longer exists."

  // Wirer — missing action on event / action no longer exists (later phase)
  KV1011: { category: "wirer", fixable: false }, // "Target event '{0}' no longer exists."
  // KOA-18158 — reworded: this NEVER meant "the action is missing". It is emitted
  // inside `if (dataSourceAction != null)`, so the action exists and is merely not
  // loaded on this screen. The fix is a load wire (pageInit), not re-creating it.
  // Now actually STAMPED at the emit site, so this entry can be reached at all —
  // before that, `Code` was null here and the lookup could never match.
  KV1143: { category: "wirer", fixable: false }, // "Action '{0}' is bound here but is not loaded on this screen."
  // Id↔name drift: the ActionDefinitionId resolves, but to a differently-named
  // action than the binding's object path claims. Wirer-domain, no shipped fixer.
  KV1245: { category: "wirer", fixable: false }, // "The binding '{0}' points to an action that is now named differently."
  KV1002: { category: "wirer", fixable: false }, // "The action {0} no longer exists."

  // Designer — missing component reference (later phase, needs render)
  KV1003: { category: "designer", fixable: false }, // "Target component no longer exists."
  KV1005: { category: "designer", fixable: false }, // "Selected component no longer exists."

  // Condition — operator/condition missing (later phase)
  KV1001: { category: "condition", fixable: false }, // visibility condition missing
  KV1137: { category: "condition", fixable: false }, // action condition operator missing
  KV1162: { category: "condition", fixable: false }, // editability condition operator missing
  KV1179: { category: "condition", fixable: false } // validation condition missing
};

// Freeze each entry so a reference handed out by getValidationFixability() can
// never poison the shared singleton for the other consumer (Issues panel ⇄ orchestrator).
for (const entry of Object.values(VALIDATION_FIX_MAP)) {
  Object.freeze(entry);
}
Object.freeze(VALIDATION_FIX_MAP);

/**
 * Resolve a validation code to its fix domain, or `null` when the code is
 * absent or not an AI-addressable validation.
 */
export function getValidationFixability(code: string | undefined | null): ValidationFixability | null {
  if (!code) return null;
  return VALIDATION_FIX_MAP[code] ?? null;
}

/**
 * True only when a SHIPPED AI fixer can act on this validation. Used by the
 * Issues panel to decide whether to render the "Fix with AI" button.
 */
export function isValidationFixable(validation: IValidationsModel): boolean {
  const fixability: ValidationFixability | null = getValidationFixability(validation.code);
  return fixability?.fixable === true;
}
